import { useEffect, useState } from 'react';
import { get, post, session, fmt, parseBhd, ApiError } from '../lib/api';

const DENOMS = [
  { label: 'BD 20', fils: 20000 }, { label: 'BD 10', fils: 10000 },
  { label: 'BD 5', fils: 5000 }, { label: 'BD 1', fils: 1000 },
  { label: '500 fils', fils: 500 }, { label: '100 fils', fils: 100 },
  { label: '50 fils', fils: 50 }, { label: '25 fils', fils: 25 },
  { label: '10 fils', fils: 10 }, { label: '5 fils', fils: 5 },
];

/**
 * Shift and cash control.
 *
 * BLIND CLOSE: the cashier counts the drawer WITHOUT being shown what the
 * system expects. The variance is only revealed after the count is committed.
 * Showing the expected figure first defeats the point of counting at all, and
 * it is the single most effective cash-loss control a POS can have.
 */
export default function Shift() {
  const s = session.get()!;
  const [shift, setShift] = useState<any>(null);
  const [xreport, setXreport] = useState<any>(null);
  const [float, setFloat] = useState('50.000');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [note, setNote] = useState('');
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const d = await get<any>(`/api/v1/shifts/current?storeCode=${s.storeCode}&terminalCode=${s.terminalCode}`);
      setShift(d.shift);
      if (d.shift) {
        const x = await get<any>(`/api/v1/shifts/${d.shift.id}/x-report`);
        setXreport(x);
      } else setXreport(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load shift');
    }
  }

  useEffect(() => { void refresh(); }, []);

  const counted = DENOMS.reduce((sum, d) => sum + d.fils * (counts[d.label] ?? 0), 0);

  async function open() {
    setBusy(true); setError('');
    try {
      await post('/api/v1/shifts/open', {
        storeCode: s.storeCode, terminalCode: s.terminalCode, floatMinor: parseBhd(float),
      });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open shift');
    } finally { setBusy(false); }
  }

  async function close() {
    setBusy(true); setError('');
    try {
      const used = Object.fromEntries(Object.entries(counts).filter(([, v]) => v > 0));
      const res = await post<any>(`/api/v1/shifts/${shift.id}/close`, {
        countedMinor: counted,
        denominations: Object.keys(used).length ? used : undefined,
        note: note || undefined,
      });
      setResult(res);
      setShift(null); setXreport(null); setCounts({}); setNote('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not close shift');
    } finally { setBusy(false); }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4">
      <h1 className="text-xl font-semibold">Shift &amp; cash</h1>

      {!shift && !result && (
        <div className="card p-5 max-w-md">
          <div className="font-medium mb-1">No shift open on till {s.terminalCode}</div>
          <div className="text-xs text-gray-500 mb-4">Count the opening float and open the shift.</div>
          <label className="label">Opening float (BHD)</label>
          <input className="input w-full money mb-3" value={float} onChange={(e) => setFloat(e.target.value)} />
          <button className="btn-primary w-full h-11" disabled={busy} onClick={() => void open()}>
            {busy ? 'Opening…' : 'Open shift'}
          </button>
        </div>
      )}

      {shift && (
        <>
          <div className="grid md:grid-cols-3 gap-4">
            <Stat label="Opened" value={new Date(shift.openedAt).toLocaleTimeString('en-GB')} />
            <Stat label="Opening float" value={fmt(shift.floatMinor)} money />
            <Stat label="Sales this shift" value={String(xreport?.totals?.sales ?? 0)} />
          </div>

          {xreport && (
            <div className="card">
              <div className="px-4 py-2 border-b border-gray-800 text-sm font-medium flex justify-between">
                <span>X-report (non-resetting)</span>
                <span className="text-xs text-gray-500">{new Date().toLocaleString('en-GB')}</span>
              </div>
              <div className="p-4 grid sm:grid-cols-2 gap-6 text-sm">
                <div>
                  <div className="label">Totals</div>
                  <Line k="Transactions" v={String(xreport.totals?.sales ?? 0)} />
                  <Line k="Gross" v={fmt(Number(xreport.totals?.gross ?? 0))} />
                  <Line k="VAT" v={fmt(Number(xreport.totals?.vat ?? 0))} />
                  <Line k="Discounts" v={fmt(Number(xreport.totals?.discount ?? 0))} />
                </div>
                <div>
                  <div className="label">By tender</div>
                  {(xreport.tenders ?? []).length === 0 && <div className="text-gray-600 text-xs">No payments yet</div>}
                  {(xreport.tenders ?? []).map((t: any) => (
                    <Line key={t.method} k={t.method.replace('_', ' ')} v={fmt(Number(t.total))} sub={`${t.count} txn`} />
                  ))}
                </div>
              </div>
              {xreport.expectedCashMinor === null && (
                <div className="px-4 pb-3 text-[11px] text-gray-500">
                  Expected cash is hidden — blind close policy.
                </div>
              )}
              {xreport.expectedCashMinor !== null && (
                <div className="px-4 pb-3 text-sm flex justify-between border-t border-gray-800 pt-2">
                  <span className="text-gray-400">Expected cash in drawer</span>
                  <span className="money font-semibold">{fmt(xreport.expectedCashMinor)}</span>
                </div>
              )}
            </div>
          )}

          <div className="card p-5">
            <div className="font-medium">Close shift — blind count</div>
            <div className="text-xs text-gray-500 mb-4">
              Count the drawer and enter the quantity of each note and coin. You will see
              the variance only after you commit the count.
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {DENOMS.map((d) => (
                <div key={d.label}>
                  <label className="label">{d.label}</label>
                  <input type="number" min={0} className="input w-full money"
                    value={counts[d.label] ?? ''}
                    placeholder="0"
                    onChange={(e) => setCounts((p) => ({ ...p, [d.label]: Math.max(0, Number(e.target.value) || 0) }))} />
                </div>
              ))}
            </div>

            <div className="flex items-baseline justify-between mt-4 pt-3 border-t border-gray-800">
              <span className="text-gray-400 text-sm">Counted total</span>
              <span className="money text-2xl font-bold">{fmt(counted)}</span>
            </div>

            <label className="label mt-4">Note (required if there is a variance)</label>
            <input className="input w-full" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. 500 fils short, suspect change error on receipt 000123" />

            <button className="btn-danger w-full h-11 mt-4" disabled={busy || counted <= 0} onClick={() => void close()}>
              {busy ? 'Closing…' : `Commit count and close shift · ${fmt(counted)}`}
            </button>
          </div>
        </>
      )}

      {result && (
        <div className={`card p-5 border-l-4 ${
          result.variance?.minor === 0 ? 'border-l-emerald-500' : 'border-l-amber-500'
        }`}>
          <div className="font-medium mb-3">Shift closed — Z report</div>
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="label">Counted</div>
              <div className="money text-lg">{fmt(result.shift.countedMinor)}</div>
            </div>
            <div>
              <div className="label">Expected</div>
              <div className="money text-lg">{fmt(result.shift.expectedMinor)}</div>
            </div>
            <div>
              <div className="label">Variance</div>
              <div className={`money text-lg font-bold ${
                result.variance.minor === 0 ? 'text-emerald-400'
                  : result.variance.minor > 0 ? 'text-sky-400' : 'text-red-400'
              }`}>
                {result.variance.minor > 0 ? '+' : ''}{fmt(result.variance.minor)}
                <span className="text-xs font-normal ml-2 uppercase">{result.variance.status}</span>
              </div>
            </div>
          </div>
          {result.variance.requiresExplanation && (
            <div className="mt-3 text-xs text-amber-300 bg-amber-950/40 border border-amber-900 rounded px-3 py-2">
              Variance exceeds 1.000 BHD — this will appear on the cash variance report
              for management review.
            </div>
          )}
          <button className="btn-ghost mt-4" onClick={() => { setResult(null); void refresh(); }}>Done</button>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-300 bg-red-950/50 border border-red-900 rounded-lg px-4 py-3">{error}</div>
      )}
    </div>
  );
}

function Stat({ label, value, money }: { label: string; value: string; money?: boolean }) {
  return (
    <div className="card p-4">
      <div className="label">{label}</div>
      <div className={`text-xl font-semibold ${money ? 'money' : ''}`}>{value}</div>
    </div>
  );
}

function Line({ k, v, sub }: { k: string; v: string; sub?: string }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-gray-400 capitalize">{k}{sub && <span className="text-gray-600 text-xs ml-1">({sub})</span>}</span>
      <span className="money">{v}</span>
    </div>
  );
}
